import { getCoreRowModel, getSortedRowModel, Table, useReactTable } from "@tanstack/react-table";
import { ApiVenueMap } from "griptonite-api-client";
import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePrevious } from "react-use";
import { createStore, ExtractState } from "zustand";
import { combine, devtools } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";

import { useVenueCompetitionsQuery } from "@/hooks/competitions";
import { useRouteLabelConfigQuery } from "@/hooks/routeLabels";
import useLocalStorage from "@/hooks/useLocalStorage.ts";

import { Route } from "../../core/types";
import { useVenueRoutesByClimbTypeQuery } from "../../hooks/routes";
import { useCurrentClimbType, useCurrentVenueQuery, useCurrentVenueUuid } from "../../hooks/venues";
import * as columns from "../RoutesTable/columnDefs";
import {
  RouteFacetsProvider,
  RoutesTableStoreProvider,
  useRoutesTableStore,
} from "../RoutesTable/RoutesProvider2";
import useFilteredRoutes, {
  filterParamsToColumnFilters,
  RoutesFacets,
  useRoutesFacets,
} from "../RoutesTable/useFilteredRoutes";
import { ContextMenuProps } from "./types";
import { useRoutesUiStore } from "./useRoutesUiStore";

export type TableType = Table<Route>;

export type RoutesUiContext = {
  routes: Route[] | undefined;
  filteredRoutes: Route[];
  filteredRouteUuids: Set<string>;
  routesFacets: RoutesFacets;
  table: TableType;
  onAddRoute: (routeData?: Partial<Route>) => void;
  canChangeSelection: boolean;
  routesPending: boolean;
};

export const RoutesUiContext = createContext<RoutesUiContext | undefined>(undefined);

const emptyArray: never[] = [];

export type MapPositionEdit = (
  | {
      map: string;
      x: number;
      y: number;
    }
  | {
      map: null;
      x: null;
      y: null;
    }
) & {
  line?: string | null;
};

// export const useRoutesUi = () => {
//   const { data: currentVenue } = useCurrentVenueQuery();
//   const climbType = useCurrentClimbType();

//   const { data: routes } = useVenueRoutesByClimbTypeQuery(
//     currentVenue?.uuid,
//     climbType
//   );

//   const store = useUiStore();

//   return useMemo(
//     () => ({
//       ...store,
//       routes,
//       currentVenue,
//       climbType,
//     }),
//     [store, routes, currentVenue, climbType]
//   );
// };

export type RoutesUiStoreState = ExtractState<ReturnType<typeof createRoutesUiStore>>;

const createRoutesUiStore = () => {
  return createStore(
    devtools(
      combine(
        {
          // selectedRouteUuids: new Set<string>(),
          // highlightedRouteUuids: new Set<string>(),

          displayRouteSidebar: false,
          displayFilterSidebar: false,

          mapPositionEdits: {} as Record<string, MapPositionEdit>,
          mapMode: "select" as "select" | "pan",
          currentlyDraggingRouteUuid: null as string | null,
          highlightedLineUuid: null as string | null,

          // Used for doing shift-selection in the table
          // lastSelectedRouteUuid: null as string | null,

          contextMenu: null as ContextMenuProps | null,

          displayedMapUuids: undefined as undefined | string[],

          // filterParams: {} as FilterQuery,

          // displayedColumns: undefined as Set<string> | undefined,

          routeFormEdits: {} as Record<string, Partial<Route>>,
          routeFormIsDirty: false,

          createRouteData: undefined as ({ uuid: string } & Partial<Route>) | undefined,
        },
        (set) => ({
          // setSelectedRouteUuids: (ids: string[]) => {
          //   console.log("setSelectedRouteUuids", ids);
          //   return set(() => ({
          //     selectedRouteUuids: new Set(ids),
          //     lastSelectedRouteUuid: ids[ids.length - 1] || null,
          //   }));
          // },
          // setHighlightedRouteUuids: (ids: string[]) =>
          //   set(() => ({
          //     highlightedRouteUuids: new Set(ids),
          //   })),
          setMapMode: (mode: "select" | "pan") => {
            set(() => ({
              mapMode: mode,
            }));
          },
          // TODO: use polyfill and replace with intersection/difference
          // addToSelection: (ids: string[]) =>
          //   set((state) => {
          //     const newUuids = [...state.selectedRouteUuids, ...ids];
          //     console.log({ newUuids });
          //     return {
          //       selectedRouteUuids: new Set(newUuids),
          //       lastSelectedRouteUuid: newUuids[newUuids.length - 1] || null,
          //     };
          //   }),
          // removeFromSelection: (ids: string[]) =>
          //   set((state) => {
          //     const newUuids = [...state.selectedRouteUuids].filter((id) => {
          //       return !ids.includes(id);
          //     });
          //     return {
          //       selectedRouteUuids: new Set(newUuids),
          //       lastSelectedRouteUuid: newUuids[newUuids.length - 1] || null,
          //     };
          //   }),
          setHighlightedLineUuid: (lineUuid: string | null) =>
            set(() => ({
              highlightedLineUuid: lineUuid,
            })),
          setDisplayRouteSidebar: (display: boolean) => set({ displayRouteSidebar: display }),
          setDisplayFilterSidebar: (display: boolean) => set({ displayFilterSidebar: display }),
          setMapPositionEdit: (routeUuid: string, edit: MapPositionEdit) =>
            set((state) => ({
              mapPositionEdits: {
                ...state.mapPositionEdits,
                [routeUuid]: edit,
              },
            })),
          clearMapPositionEdit: (routeUuid: string) => {
            set((state) => {
              const { [routeUuid]: _, ...rest } = state.mapPositionEdits;
              return { mapPositionEdits: rest };
            });
          },
          setRouteFormEdit: (routeUuid: string, edit: Partial<Route>) =>
            set((state) => ({
              routeFormEdits: {
                ...state.routeFormEdits,
                [routeUuid]: edit,
              },
            })),
          clearRouteFormEdit: (routeUuid: string) => {
            set((state) => {
              const { [routeUuid]: _, ...rest } = state.routeFormEdits;
              return { routeFormEdits: rest };
            });
          },
          setRouteFormIsDirty: (dirty: boolean) => {
            set(() => ({
              routeFormIsDirty: dirty,
            }));
          },
          setCurrentlyDraggingRouteUuid: (routeUuid: string | null) => {
            set(() => ({
              currentlyDraggingRouteUuid: routeUuid,
            }));
          },
          setContextMenu: (contextMenu: ContextMenuProps | null) => {
            set(() => ({
              contextMenu,
            }));
          },
          setDisplayedMapUuids: (uuids: string[] | undefined) =>
            set(() => ({
              displayedMapUuids: uuids,
            })),

          // setFilterParam: <K extends keyof FilterQuery>(
          //   key: K,
          //   value: FilterQuery[K]
          // ) => {
          //   set((state) => ({
          //     filterParams: {
          //       ...state.filterParams,
          //       [key]: value,
          //     },
          //   }));
          // },
          // removeFilterParam: <K extends keyof FilterQuery>(key: K) => {
          //   set((state) => {
          //     const { [key]: _, ...rest } = state.filterParams;
          //     return { filterParams: rest };
          //   });
          // },
          // clearFilterParams: () => {
          //   set(() => ({
          //     filterParams: {},
          //   }));
          // },
          // setDisplayedColumns: (columns: Set<string>) => {
          //   set(() => ({
          //     displayedColumns: columns,
          //   }));
          // },
          // setColumnDisplay: (column: string, display: boolean) => {
          //   set((state) => {
          //     const displayedColumns = new Set(state.displayedColumns);
          //     if (display) {
          //       displayedColumns.add(column);
          //     } else {
          //       displayedColumns.delete(column);
          //     }
          //     return {
          //       displayedColumns,
          //     };
          //   });
          // },
          setCreateRouteData: (data: ({ uuid: string } & Partial<Route>) | undefined) => {
            set(() => ({
              createRouteData: data,
            }));
          },
        }),
      ),
    ),
  );
};

export const RoutesUiStoreContext = createContext<
  ReturnType<typeof createRoutesUiStore> | undefined
>(undefined);

export const RoutesUiStoreContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [store] = useState(createRoutesUiStore);
  return <RoutesUiStoreContext.Provider value={store}>{children}</RoutesUiStoreContext.Provider>;
};

export const useRoutesUiContext = () => {
  const context = useContext(RoutesUiContext);
  if (!context) {
    throw new Error("useRoutesUiContext must be used within a RoutesUiContext");
  }
  return context;
};

export const RoutesUiContextProviderInner = ({ children }: { children: React.ReactNode }) => {
  const { data: currentVenue } = useCurrentVenueQuery();
  const currentVenueUuid = useCurrentVenueUuid();
  const climbType = useCurrentClimbType();
  // oxlint-disable-next-line prefer-const
  let { data: routes, isPending: routesPending } = useVenueRoutesByClimbTypeQuery(
    currentVenueUuid,
    climbType,
  );
  routes = useMemo(() => routes?.filter((route) => route.startTime), [routes]);

  const {
    setDisplayRouteSidebar,
    routeFormIsDirty,
    setCreateRouteData,
    createRouteData,
    displayedMapUuids,
  } = useRoutesUiStore();

  const { selectedRouteUuids, setSelectedRouteUuids, filterParams } = useRoutesTableStore(
    useShallow((state) => ({
      selectedRouteUuids: state.selectedRouteUuids,
      setSelectedRouteUuids: state.setSelectedRouteUuids,
      filterParams: state.filterParams,
    })),
  );

  const canChangeSelection = useMemo(() => {
    return !routeFormIsDirty;
  }, [routeFormIsDirty]);

  const selectedRouteUuidsRef = useRef(selectedRouteUuids);
  useLayoutEffect(() => {
    selectedRouteUuidsRef.current = selectedRouteUuids;
  }, [selectedRouteUuids]);

  const filteredRoutes = useFilteredRoutes(routes || emptyArray, filterParams);
  const filteredRouteUuids = useMemo(
    () => new Set(filteredRoutes.map((r) => r.uuid)),
    [filteredRoutes],
  );

  /**
   * When the filtered routes change, we need to make sure that the selected routes
   * are still in the filtered set. If they are not, we remove them from the selection.
   */
  useEffect(() => {
    const filtered = [...selectedRouteUuidsRef.current].filter((x) => filteredRouteUuids.has(x));
    if (filtered.length !== selectedRouteUuidsRef.current.size) {
      setSelectedRouteUuids(filtered);
    }
  }, [filteredRouteUuids, setSelectedRouteUuids]);

  const routesFacets = useRoutesFacets(routes || emptyArray);

  // Automatically show the sidebar when routes are selected.
  // The debounce is added to help stop the sidebar fade in from moving the pins around
  // when the user is in the middle of selecting multiple routes.
  useEffect(() => {
    if (selectedRouteUuids.size || createRouteData) {
      setDisplayRouteSidebar(true);
    } else {
      setDisplayRouteSidebar(false);
    }
  }, [selectedRouteUuids, setDisplayRouteSidebar, createRouteData]);

  //convert the selected routes set to an object for react-table
  const tableRowSelection = useMemo(() => {
    return Object.fromEntries(Array.from(selectedRouteUuids, (uuid) => [uuid, true])) as Record<
      string,
      boolean
    >;
  }, [selectedRouteUuids]);

  const { data: venueCompetitions } = useVenueCompetitionsQuery(currentVenueUuid);
  const competitionsCount = useMemo(() => {
    return venueCompetitions?.filter((c) => c.status !== "ENDED").length || 0;
  }, [venueCompetitions]);

  // Only venues that have set up labels get the column.
  const { data: labelConfig } = useRouteLabelConfigQuery();
  const hasLabels = !!(
    labelConfig?.groups.some((group) => group.isActive) ||
    labelConfig?.labels.some((label) => label.isActive)
  );

  const COLUMN_VISIBILITY_KEY = "routesTableColumnVisibility";
  const { [COLUMN_VISIBILITY_KEY]: savedColumnVisibility, setItem: setLocalStorageItem } =
    useLocalStorage({ keys: [COLUMN_VISIBILITY_KEY] });

  const defaultColumnVisibility: Record<string, boolean> = {
    [columns.stylesColumn.id || ""]: false,
    [columns.skillsColumn.id || ""]: false,
    [columns.holdsColumn.id || ""]: false,
    [columns.commentsColumn.id || ""]: false,
    [columns.labelsColumn.id || ""]: false,
    [columns.quickdrawCountColumn.id || ""]: false,
    [columns.heightColumn.id || ""]: false,
  };

  const columnVisibility = useMemo(
    () => ({
      ...defaultColumnVisibility,
      ...((savedColumnVisibility as Record<string, boolean> | null) ?? {}),
    }),
    // oxlint-disable-next-line react/exhaustive-deps
    [savedColumnVisibility],
  );

  const tableColumns = useMemo(
    () => [
      columns.selectionColumn,
      columns.routeColumn,
      ...(climbType === "BOULDER" ? [columns.circuitColumn] : [columns.lineColumn]),
      columns.gradeColumn,
      columns.setterColumn,
      columns.mapColumn,
      climbType === "BOULDER" ? columns.zoneColumn : columns.sportZoneColumn,
      columns.ageColumn,
      ...(competitionsCount ? [columns.competitionCategoriesColumn] : []),
      columns.scheduledStripColumn,
      columns.starRatingColumn,
      columns.gradeFeedbackColumn,
      columns.commentsColumn,
      columns.stylesColumn,
      columns.skillsColumn,
      columns.holdsColumn,
      ...(hasLabels ? [columns.labelsColumn] : []),
      ...(climbType === "SPORT" ? [columns.quickdrawCountColumn, columns.heightColumn] : []),
    ],
    [climbType, competitionsCount, hasLabels],
  );

  const table = useReactTable({
    getRowId: (row) => row.uuid,
    columns: tableColumns,
    data: filteredRoutes || emptyArray,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualFiltering: true,
    enableSortingRemoval: false,
    initialState: {
      sorting: [
        {
          id: "name",
          desc: false,
        },
      ],
    },
    state: {
      rowSelection: tableRowSelection,
      columnFilters: filterParamsToColumnFilters(filterParams, tableColumns),
      columnVisibility,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility = updater instanceof Function ? updater(columnVisibility) : updater;
      // Merge with existing stored state so sport-specific columns (quickdrawCount, height)
      // are preserved even when currently in boulder mode (where those columns don't exist).
      setLocalStorageItem(COLUMN_VISIBILITY_KEY, {
        ...columnVisibility,
        ...newVisibility,
      });
    },
    enableRowSelection: canChangeSelection,
    onRowSelectionChange: (updater) => {
      const selectedRowIds = updater instanceof Function ? updater(tableRowSelection) : updater;
      console.log("selectedRowIds", selectedRowIds);
      if (canChangeSelection) {
        setSelectedRouteUuids(
          Object.entries(selectedRowIds)
            .filter(([, v]) => v)
            .map(([k]) => k),
        );
      }
    },
  });

  const onAddRoute = useCallback(
    (routeData?: Partial<Route>) => {
      setSelectedRouteUuids([]);
      if (climbType === "BOULDER") {
        const mapUuid = routeData?.map || displayedMapUuids?.[0] || currentVenue?.maps[0].uuid;
        const map = currentVenue?.maps.find((m) => m.uuid === mapUuid);
        const mapPosition =
          routeData?.mapPosition ||
          (map ? { x: map.width / 2, y: map.height / 2 } : { x: 100, y: 100 });
        setCreateRouteData({
          ...routeData,
          uuid: "CREATING",
          map: mapUuid,
          mapPosition,
          climbType,
          gradeSystem: currentVenue?.boulderGradeSystem ?? undefined,
          startTime: "NOW",
        });
      } else {
        setCreateRouteData({
          ...routeData,
          uuid: "CREATING",
          climbType,
          gradeSystem: currentVenue?.sportGradeSystem ?? undefined,
          startTime: "NOW",
        });
      }
      setDisplayRouteSidebar(true);
    },
    [
      setSelectedRouteUuids,
      setCreateRouteData,
      setDisplayRouteSidebar,
      displayedMapUuids,
      currentVenue?.maps,
      climbType,
      currentVenue?.boulderGradeSystem,
      currentVenue?.sportGradeSystem,
    ],
  );

  const tableState = table.getState();
  const tableContextValue = useMemo(
    () => ({
      routes,
      filteredRoutes,
      filteredRouteUuids,
      routesFacets,
      table,
      onAddRoute,
      canChangeSelection,
      routesPending,
    }),
    // oxlint-disable-next-line react/exhaustive-deps
    [
      routes,
      table,
      filteredRoutes,
      filteredRouteUuids,
      onAddRoute,
      // tableState here is needed to trigger a re-render when the table state changes.
      tableState,
      canChangeSelection,
      routesPending,
    ],
  );

  return (
    <RoutesUiContext.Provider value={tableContextValue}>
      <RouteFacetsProvider value={routesFacets}>{children}</RouteFacetsProvider>
    </RoutesUiContext.Provider>
  );
};

export const RoutesScreenProvider = ({ children }: { children: ReactNode }) => {
  return (
    <RoutesTableStoreProvider>
      <RoutesUiContextProviderInner>{children}</RoutesUiContextProviderInner>
    </RoutesTableStoreProvider>
  );
};

export const useDisplayedMapsWithDefault = () => {
  const { setDisplayedMapUuids, displayedMapUuids } = useRoutesUiStore(
    useShallow((state) => ({
      setDisplayedMapUuids: state.setDisplayedMapUuids,
      displayedMapUuids: state.displayedMapUuids,
    })),
  );
  const { routes } = useRoutesUiContext();
  const selectedRouteUuids = useRoutesTableStore((state) => state.selectedRouteUuids);
  const { data: venue, isPending: venueIsPending } = useCurrentVenueQuery();
  const climbType = useCurrentClimbType();

  const defaultMapsRef = useRef([] as ApiVenueMap[]);
  const defaultMaps = useMemo(() => {
    const routesByMapUuid = groupBy(
      routes?.filter((r) => r.climbType === climbType),
      (route) => route.map,
    );
    const mapsWithCount =
      venue?.maps
        .filter((m) => m.available)
        .map((map) => ({
          ...map,
          routesCount: routesByMapUuid[map.uuid]?.length || 0,
        })) || emptyArray;
    const defaultMaps = sortBy(
      mapsWithCount.filter((m) => m.routesCount > 0),
      (map) => -map.routesCount,
    ).slice(0, 2);
    defaultMapsRef.current = defaultMaps;
    return defaultMaps;
  }, [routes, venue?.maps, climbType]);

  const displayedMapUuidsRef = useRef(displayedMapUuids);
  useEffect(() => {
    displayedMapUuidsRef.current = displayedMapUuids;
  }, [displayedMapUuids]);

  const routesRef = useRef(routes);
  useEffect(() => {
    routesRef.current = routes;
  }, [routes]);

  const prevVenueUuid = usePrevious(venue?.uuid);

  useEffect(() => {
    const selectedRouteMaps = new Set<string>(
      routesRef.current
        ?.filter((r) => selectedRouteUuids.has(r.uuid))
        .map((r) => r.map)
        .filter((m) => !!m) as string[],
    );
    if (selectedRouteMaps.size) {
      const newDisplayedMapUuids = [
        ...(displayedMapUuidsRef.current || defaultMapsRef.current.map((m) => m.uuid)),
      ];
      selectedRouteMaps.forEach((uuid) => {
        if (!newDisplayedMapUuids.includes(uuid)) {
          newDisplayedMapUuids.unshift(uuid);
        }
      });
      setDisplayedMapUuids(newDisplayedMapUuids.slice(0, 3));
    }
  }, [selectedRouteUuids, venue?.uuid, prevVenueUuid, setDisplayedMapUuids]);

  return useMemo(() => {
    return {
      maps: displayedMapUuids
        ? (displayedMapUuids
            .map((uuid) => venue?.maps.find((m) => m.uuid === uuid))
            .filter((m) => m) as ApiVenueMap[])
        : defaultMaps,
      defaultMaps,
      isPending: venueIsPending,
    };
  }, [venueIsPending, displayedMapUuids, venue?.maps, defaultMaps]);
};
